import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios'
import type { ArgNotValid, RefreshTokenVO, ResponseBodyEntity } from '@/types/api'
import { tokenStorage } from '@/api/tokenStorage'
import { endsSession } from '@/api/sessionOutcome'

/**
 * An error carrying the backend's error code.
 *
 * The code is the contract; `message` is operator-readable detail and must not be branched on. This
 * is why the backend wraps everything in one envelope — the client reads one field.
 */
export class ApiError extends Error {
  readonly code: string

  readonly status: number

  readonly fieldErrors: ArgNotValid | null

  constructor(code: string, message: string, status: number, fieldErrors: ArgNotValid | null) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.fieldErrors = fieldErrors
  }
}

/** Endpoints that must not trigger the refresh-and-retry dance. */
const NO_RETRY_PATHS = ['/api/v1/auth/login', '/api/v1/auth/google', '/api/v1/auth/refresh']

/** Called when the session is definitively gone, so the app can route to the login screen. */
let onSessionExpired: (() => void) | null = null

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler
}

const client: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' }
})

client.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken()
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * A single in-flight refresh, shared by every request that got a 401 at the same time. Without this
 * a page with six parallel requests would fire six refreshes and, because renewal slides the token
 * forward, race each other.
 */
let refreshInFlight: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken()
  if (!refreshToken) {
    throw new ApiError('auth.refresh_token.missing', 'Not signed in', 401, null)
  }
  const response = await client.post<ResponseBodyEntity<RefreshTokenVO>>(
    '/api/v1/auth/refresh',
    null,
    { headers: { Authorization: `Bearer ${refreshToken}` } }
  )
  const accessToken = response.data.data.accessToken
  tokenStorage.setAccessToken(accessToken)
  return accessToken
}

function toApiError(error: AxiosError<ResponseBodyEntity<unknown>>): ApiError {
  const status = error.response?.status ?? 0
  const body = error.response?.data
  if (body && typeof body.code === 'string') {
    const fieldErrors =
      body.code === 'ARGUMENT_NOT_VALID' && body.data ? (body.data as ArgNotValid) : null
    return new ApiError(body.code, body.message || error.message, status, fieldErrors)
  }
  // No envelope: a network failure, a timeout, or a proxy error. Named distinctly so the UI can
  // say "cannot reach the server" instead of blaming the user's input.
  return new ApiError(status === 0 ? 'network.unreachable' : 'internal.error', error.message, status, null)
}

/**
 * Unwraps the envelope and returns `data`. Any non-OK response becomes a thrown {@link ApiError},
 * so callers never have to check a success flag.
 */
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const url = config.url ?? ''
  try {
    const response = await client.request<ResponseBodyEntity<T>>(config)
    return response.data.data
  } catch (error) {
    const apiError = toApiError(error as AxiosError<ResponseBodyEntity<unknown>>)

    const canRetry = apiError.status === 401 && !NO_RETRY_PATHS.some((path) => url.startsWith(path))
    if (!canRetry) {
      throw apiError
    }

    return retryAfterRefresh(
      apiError,
      async (accessToken) =>
        (
          await client.request<ResponseBodyEntity<T>>({
            ...config,
            headers: { ...config.headers, Authorization: `Bearer ${accessToken}` }
          })
        ).data.data,
      (retryError) => toApiError(retryError as AxiosError<ResponseBodyEntity<unknown>>)
    )
  }
}

/**
 * Renews the access token and sends the request again, for {@link request} and {@link requestBlob} alike.
 *
 * Which answers end the session is {@link endsSession}. Every other failure, of the renewal or of the retry, is
 * thrown to the page as itself.
 */
async function retryAfterRefresh<T>(
  unauthorized: ApiError,
  retry: (accessToken: string) => Promise<T>,
  toError: (error: unknown) => ApiError | Promise<ApiError>
): Promise<T> {
  let accessToken: string
  try {
    refreshInFlight = refreshInFlight ?? refreshAccessToken()
    accessToken = await refreshInFlight
  } catch (refreshError) {
    const apiError =
      refreshError instanceof ApiError
        ? refreshError
        : toApiError(refreshError as AxiosError<ResponseBodyEntity<unknown>>)
    if (endsSession('refresh', apiError.status)) {
      endSession()
      throw unauthorized
    }
    throw apiError
  } finally {
    refreshInFlight = null
  }
  try {
    return await retry(accessToken)
  } catch (retryError) {
    const apiError = await toError(retryError)
    if (endsSession('retry', apiError.status)) {
      endSession()
    }
    throw apiError
  }
}

function endSession(): void {
  tokenStorage.clear()
  if (onSessionExpired) {
    onSessionExpired()
  }
}

/** Reports transfer progress. `total` is absent when the server sent no length. */
export type ProgressHandler = (loaded: number, total?: number) => void

/**
 * Sends a file as `multipart/form-data`, with progress.
 *
 * > 🔴 **`Content-Type` is deleted, not set.** The instance default is `application/json`, and axios only
 * > computes the multipart type — with its boundary — when no type is already present. Leaving the default in
 * > place sends a `FormData` body labelled as JSON, which the server rejects as unparseable; setting
 * > `multipart/form-data` by hand is worse, because the boundary is missing and the parse fails on the server
 * > with nothing wrong on the wire to see. `null` is how axios v1 removes a header.
 *
 * Long by nature, so the request timeout is lifted: a 200 MB installer over a factory link takes minutes, and
 * the default 20 s would abort a transfer that was working.
 */
export async function upload<T>(url: string, form: FormData, onProgress?: ProgressHandler): Promise<T> {
  return request<T>({
    method: 'POST',
    url,
    data: form,
    timeout: 0,
    headers: { 'Content-Type': null } as unknown as Record<string, string>,
    onUploadProgress: (event) => onProgress?.(event.loaded, event.total)
  })
}

/** A signed storage upload that did not finish. Not an {@link ApiError}: storage answers in XML, not the envelope. */
export class StorageUploadError extends Error {
  /** Absent when the request never got an answer, which is what a missing CORS rule looks like. */
  readonly status: number | null

  constructor(status: number | null) {
    super('storage upload failed')
    this.name = 'StorageUploadError'
    this.status = status
  }
}

/**
 * `PUT`s a file to a URL this service signed, straight to storage.
 *
 * > 🔴 **Plain `axios`, never `client`.** The instance adds `Authorization: Bearer …` to every request, and a
 * > signed URL already carries its credential in the query — storage refuses a request that has both.
 * > It also sets a JSON `Content-Type`, which has to be the one the URL was signed for instead.
 *
 * The timeout is lifted for the reason `upload` lifts it, and there is no retry: a 403 here is an expired or
 * mismatched signature, which sending the same bytes again cannot fix.
 */
export async function putToSignedUrl(
  url: string,
  file: File,
  contentType: string,
  onProgress?: ProgressHandler
): Promise<void> {
  try {
    await axios.put(url, file, {
      timeout: 0,
      headers: { 'Content-Type': contentType },
      onUploadProgress: (event) => onProgress?.(event.loaded, event.total)
    })
  } catch (error) {
    throw new StorageUploadError(error instanceof AxiosError ? (error.response?.status ?? null) : null)
  }
}

/**
 * Fetches a binary body — the one response in this API that is not the envelope.
 *
 * Deliberately not routed through {@link request}: that unwraps `data.data`, which a blob does not have, and its
 * error path reads `code` off a JSON body that arrives here as a `Blob`. The refresh-on-401 is shared through
 * {@link retryAfterRefresh}; only how a response and an error are read differs.
 *
 * The 401 retry matters more here than anywhere else: a download is the longest-lived request in the app, and a
 * token that was valid when it started may not be by the time it is retried.
 */
export async function download(
  url: string,
  onProgress?: ProgressHandler
): Promise<Blob> {
  return requestBlob({
    method: 'GET',
    url,
    responseType: 'blob',
    timeout: 0,
    onDownloadProgress: (event) => onProgress?.(event.loaded, event.total)
  })
}

/**
 * Fetches a binary body generated from a request body — a console list exported with the filters it is showing.
 *
 * POST because the filters are a body, as they are for the list itself. The same refresh-on-401 and the same
 * envelope-in-a-blob error path as {@link download}.
 */
export async function downloadPost(url: string, data: unknown): Promise<Blob> {
  return requestBlob({ method: 'POST', url, data, responseType: 'blob', timeout: 0 })
}

async function requestBlob(config: AxiosRequestConfig): Promise<Blob> {
  try {
    return (await client.request<Blob>(config)).data
  } catch (error) {
    const apiError = await blobToApiError(error as AxiosError<Blob>)
    if (apiError.status !== 401) {
      throw apiError
    }
    return retryAfterRefresh(
      apiError,
      async (accessToken) =>
        (await client.request<Blob>({ ...config, headers: { Authorization: `Bearer ${accessToken}` } })).data,
      (retryError) => blobToApiError(retryError as AxiosError<Blob>)
    )
  }
}

/**
 * Turns a failed blob request into an {@link ApiError}.
 *
 * The envelope is there — a failure happens before the first byte of the file — but `responseType: 'blob'`
 * applies to every response, so it arrives as a `Blob` and has to be read back as text. Without this a real
 * error code would be lost and every failed download would look like a network problem.
 */
async function blobToApiError(error: AxiosError<Blob>): Promise<ApiError> {
  const status = error.response?.status ?? 0
  const body = error.response?.data
  if (body instanceof Blob && body.size > 0) {
    try {
      const envelope = JSON.parse(await body.text()) as { code?: string; message?: string }
      if (typeof envelope.code === 'string') {
        return new ApiError(envelope.code, envelope.message || error.message, status, null)
      }
    } catch {
      // Not JSON. Falls through to the generic error below, which is the honest answer for a body this
      // client cannot interpret.
    }
  }
  return new ApiError(
    status === 0 ? 'network.unreachable' : 'internal.error',
    error.message,
    status,
    null
  )
}

export const http = {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, method: 'GET', url })
  },
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, method: 'POST', url, data })
  },
  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, method: 'PUT', url, data })
  },
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, method: 'DELETE', url })
  }
}
